import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { cloneDeep } from 'lodash'
import { logger } from 'shared/lib'
import { type IAlert } from 'shared/ui/Alert/types'
import { RequestCancelAxios } from 'shared/api/lib'
import type { IContainerHeaderProps } from 'shared/ui'
import { ContactsApi } from 'entities/Contacts'
import { type IResponseConversation } from 'entities/Conversation/api/types'
import { type Contact } from 'entities/Contacts/model/Contact'
import { type IResponseContact } from 'entities/Contacts/api/types'
import { ConversationsApi } from 'entities/Conversation'
import { UsersApi } from 'entities/Users'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ContactInfoFormStore } from 'widgets/ContactsInfoForm'
import { ContactsIntegrationsStore } from 'widgets/ContactsDetails/ui/ContactsIntegrations/store/contactsIntegrationsStore'
import { ContactNoteStore } from 'widgets/ContactsDetails/ui/ContactsNotesForm'
import type {
  IContactsDetailsStoreConfig,
  IContactsDetailsVariant,
  IViewTypes,
} from 'widgets/ContactsDetails/store/types'
import { ContactsConversationOpenStore } from 'widgets/ContactsDetails/ui/ContactsConversationOpen'
import { ContactsProfileCardStore } from 'widgets/ContactsDetails/ui/ContactsProfileCard'
import { ConversationInfoStore } from 'widgets/ContactsDetails/ui/ConversationInfo'
import { ContactsTagsFormStore } from 'widgets/ContactsDetails/ui/ContactsTagsForm'
import {
  contactsQuickLinksHeader,
  ContactsQuickLinksStore,
} from 'widgets/ContactsDetails/ui/ContactsQuickLinks'
import { ContactsGroupCardStore } from 'widgets/ContactsDetails/ui/ContactsGroupCard'
import { ContactsSurveysStore } from 'widgets/ContactsDetails/ui/ContactsSurveys'
import { ConversationsHistoryMessagesStore } from 'widgets/ContactsDetails/ui/ContactsConversationsHistory'
import { contactsDetailsGlobalStore } from './contactsDetailsGlobalStore'

const defaultDraggableItems = [
  'conversation_info',
  'contacts_group',
  'contacts_info',
  'contacts_integrations',
  'contacts_surveys',
  'contacts_tags',
  'contacts_notes',
  'contacts_quick_links',
]

export class ContactsDetailsStore {
  private _id = nanoid()
  private _contactId: number | null = null
  private _conversationId: number | null = null
  private _inboxId: number | null = null
  private _loading = true

  private _variant: IContactsDetailsVariant = 'conversation'
  private _view: IViewTypes = 'main'

  private _contactsDetailsGlobalStore = contactsDetailsGlobalStore
  private _conversationsHistoryMessagesStore = new ConversationsHistoryMessagesStore(this)
  private readonly _contactsConversationOpenStore: ContactsConversationOpenStore | null = null
  private readonly _contactNoteStore: ContactNoteStore
  private _contactsProfileCardStore = new ContactsProfileCardStore(this)
  private _conversationInfoStore = new ConversationInfoStore(this)
  private _contactsTagsFormStore = new ContactsTagsFormStore(this)
  private _contactsQuickLinksStore = new ContactsQuickLinksStore(this)
  private _contactsGroupCardStore = new ContactsGroupCardStore(this)
  private _contactsSurveysStore = new ContactsSurveysStore(this)
  private readonly _contactIntegrationsStore: ContactsIntegrationsStore
  private readonly _contactInfoFormStore: ContactInfoFormStore

  private _disposeAlert: IReactionDisposer | null = null
  private _disposeContactsDetailsToggle: IReactionDisposer | null = null
  private _disposeContactUpdated: IReactionDisposer | null = null
  private _disposeExternalContactUpdated: IReactionDisposer | null = null
  private _disposeConversation: IReactionDisposer | null = null
  private _disposeContacts: IReactionDisposer | null = null
  private _disposeView: IReactionDisposer | null = null

  private _alert: IAlert | null = null
  private _config: IContactsDetailsStoreConfig | null = null
  private _draggableItems: string[] = defaultDraggableItems
  private _sectionsItems: string[] = defaultDraggableItems
  private _cancelRequestInitDraggable = new RequestCancelAxios()
  private _cancelRequestSearchConversation = new RequestCancelAxios()

  constructor(config: IContactsDetailsStoreConfig, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._contactIntegrationsStore = new ContactsIntegrationsStore(this._entities)
    this._contactInfoFormStore = new ContactInfoFormStore(this._entities)

    if (config.variant === 'open_conversation') {
      this._contactsConversationOpenStore = new ContactsConversationOpenStore(this, this._entities)
    }

    this._contactNoteStore = new ContactNoteStore(this, this._entities)
    this._view = 'main'

    this.initReactions()
    this.setConfig(config)
  }

  get entities() {
    return this._entities
  }

  get conversationStore() {
    return this._entities.conversationStore
  }

  handleUpdateContact = (item: IResponseContact) => {
    this._entities.contactsStore.updateItem(item)

    const contact = this._entities.contactsStore.getItem(item.id)

    if (contact) {
      this._contactId = contact.id
      this._config?.onUpdateContact?.(contact)
      this._contactInfoFormStore.syncFields(contact.id)
    }
  }

  initReactions = () => {
    this.reactionConversation()
    this.reactionContacts()
    this.reactionContactsDetailsToggle()
    this.reactionContactUpdated()
    this.reactionExternalContactUpdated()
    this.reactionAlert()
    this.reactionView()
    this._contactsGroupCardStore.reactionContacts()
  }

  setConfig = (config: IContactsDetailsStoreConfig) => {
    const contactId = typeof config.contactId === 'number' ? config.contactId : null
    const conversationId = typeof config.conversationId === 'number' ? config.conversationId : null
    const inboxId = config.inboxId || null
    const messageId = config.messageId || null
    this._variant = config.variant || 'conversation'

    if (contactId) this.setContactId(contactId)
    if (conversationId) this.setConversationId(conversationId)
    if (inboxId) this.setInboxId(inboxId)
    if (messageId) this.setMessageId(messageId)

    this._conversationInfoStore.setConfig({
      onSync: config?.onSync,
    })

    this._contactInfoFormStore.setConfig({
      onChangeAlert: (item) => {
        this.setAlert(item)
      },
      addNewContactLocallyAfterCreate: config.addNewContactLocallyAfterCreate,
      isVariantCreateContactModal: this.isVariantCreateContactModal,
      onChangeIsFirstEditMode: this.config?.onChangeIsFirstEditMode,
      handleUpdateContact: this.handleUpdateContact,
      onSync: config?.onSync,
      variantView: 'contactsDetails',
    })

    this.contactChoosePhoneModalStore.setConfig({
      onChangeAlert: (item) => {
        this.setAlert(item)
      },
    })

    this._contactIntegrationsStore.setConfig({
      resetActiveAndCloseDetailsIsMobile: config.resetActiveAndCloseDetailsIsMobile,
      onOpenView: (value) => {
        this.openView(value)
      },
      onCloseView: () => {
        this.closeView()
      },
      onSync: config?.onSync,
      blockKeyView: 'integration',
    })

    this._contactNoteStore.setConfig({
      onSync: config?.onSync,
    })

    this._contactsTagsFormStore.setConfig({
      onSync: config?.onSync,
    })

    this._contactsProfileCardStore.setConfig({
      contactId: this._contactId,
      contactsIds: this.contactsIds,
      variant: config.variant,
      resetActiveAndCloseDetailsIsMobile: config.resetActiveAndCloseDetailsIsMobile,
      handleOptIn: config.handleOptIn,
      openView: this.openView,
      enableChat: config.profileCardProps?.enableChat,
      enableCall: config.profileCardProps?.enableCall,
      onClickMessage: config.profileCardProps?.onClickMessage,
      onClickCall: config.profileCardProps?.onClickCall,
      onActionCall: config.profileCardProps?.onActionCall,
    })

    if (config.sub_source) {
      this._contactsConversationOpenStore?.setSubSource(config.sub_source)
    }

    this._config = {
      ...this._config,
      ...config,
    }
  }

  setConfigContainerHeaderProps = (config: IContainerHeaderProps) => {
    if (!this._config) return

    this._config['containerHeaderProps'] = config
  }

  get contactChoosePhoneModalStore() {
    return this._entities.contactsStore.contactChoosePhoneModalStore
  }

  get draggableItems() {
    return this._draggableItems
  }

  get sectionsItems() {
    return this._sectionsItems
  }

  get config() {
    return this._config
  }

  get notesProps() {
    return this._config?.notesProps
  }

  get headerProps() {
    return this._config?.headerProps
  }

  get profileCardProps() {
    return this._config?.profileCardProps
  }

  get contactsProfileCardStore() {
    return this._contactsProfileCardStore
  }

  get contactsTagsFormStore() {
    return this._contactsTagsFormStore
  }

  get conversationInfoStore() {
    return this._conversationInfoStore
  }

  get contactsQuickLinksStore() {
    return this._contactsQuickLinksStore
  }

  get contactsSurveysStore() {
    return this._contactsSurveysStore
  }

  get contactsGroupCardStore() {
    return this._contactsGroupCardStore
  }

  get isVariantConversations() {
    return this._variant === 'conversation'
  }

  get isVariantContacts() {
    return this._variant === 'contacts'
  }

  get isVariantCreateContactModal() {
    return this._variant === 'create_contact_modal'
  }

  get isVariantOpenConversations() {
    return this._variant === 'open_conversation'
  }

  get isConversationNew() {
    if (!this.conversation) return true

    return this.conversation?.isNew
  }

  get isViewAll() {
    if (this._view === 'main') return false

    return (
      this._view === 'companies' ||
      this._view === 'deals' ||
      this._view === 'notes' ||
      this._view === 'opportunities' ||
      this._view === 'tickets'
    )
  }

  get id() {
    return this._id
  }

  get contactNoteStore() {
    return this._contactNoteStore
  }

  get view() {
    return this._view
  }

  get loading() {
    return this._loading
  }

  load = () => {
    if (this._conversationId) {
      this.loadConversation()
    } else {
      this.loadContact()
    }

    this._config?.initialized?.(this)
  }

  loadContact = async () => {
    if (!this._contactId || this._contactId <= 0) {
      runInAction(() => {
        this._loading = false
      })

      return
    }

    try {
      runInAction(() => {
        this._loading = true
      })

      let contact = this._entities.contactsStore.getItem(this._contactId)

      if (!this._conversationId && this._conversationId !== 0) {
        const inboxIds: number[] = []

        if (this._inboxId) {
          inboxIds.push(this._inboxId)
        }

        this._cancelRequestSearchConversation.init()

        const conversationResponse = await ConversationsApi.getConversationsSearch(
          {
            teams: inboxIds,
            contacts: [this._contactId],
            page: 1,
          },
          {
            cancelToken: this._cancelRequestSearchConversation.token,
          }
        ).then((data) => data.data.results[0])

        if (conversationResponse) {
          const conversation = this.conversationStore.addItem(conversationResponse)
          contact = this._entities.contactsStore.getItem(conversationResponse.contact_id)

          this.setConversationId(conversation.id)
          this.conversationStore.setCurrentItemId(conversation.id)
          this.setInboxId(conversation.inbox_id)
        } else {
          if (this._entities.inboxesStore.currentInbox?.type === 'inbox') {
            this.setInboxId(this._entities.inboxesStore.currentInbox.id)
          } else if (this._entities.inboxesStore.sharedInboxes.length === 1) {
            const inbox = this._entities.inboxesStore.sharedInboxes[0]

            this.setInboxId(inbox.id)
          }
        }
      }

      if (!contact) {
        const response = await ContactsApi.getContactById(this._contactId).then((data) => data.data)
        contact = this._entities.contactsStore.addItem(response)
      }

      if (contact) {
        this.setContactId(contact.id)
      }
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  loadConversation = async () => {
    if (!this._conversationId || this._conversationId <= 0) {
      runInAction(() => {
        this._loading = false
      })

      return
    }

    try {
      runInAction(() => {
        this._loading = true
      })

      let conversation = this.conversationStore.getItem(this._conversationId)

      if (!conversation) {
        const response = await ConversationsApi.getById(this._conversationId).then(
          (data) => data.data
        )
        conversation = this.conversationStore.addItem(response)
      }

      if (conversation) {
        this.setContactId(conversation.contact_id)
        this.setConversationId(conversation.id)
        this.setInboxId(conversation.inbox_id)
      }
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  setConversationId = (id: number | null) => {
    if (this._conversationId === id) return

    this._conversationId = id
  }

  setInboxId = (id: number) => {
    if (this._inboxId === id) return

    this._inboxId = id
    this._contactsProfileCardStore.setInboxId(id)
  }

  setMessageId = (id: number) => {
    this.contactsConversationOpenStore?.setMessageId(id)
  }

  setContactId = (id: number) => {
    if (this._contactId === id) return

    this._contactId = id
    this._contactsProfileCardStore.setContactId(id)
    this._contactInfoFormStore.setContactId(id)
    this._contactIntegrationsStore.setContactId(id)
    this._contactsConversationOpenStore?.setContactId(id)
  }

  handleUpdateConversation = (item: IResponseConversation) => {
    const conversation = this.conversationStore.getItem(item.id)

    if (conversation) {
      this._conversationId = conversation.id
      this._contactInfoFormStore.syncFields(conversation.contact_id)
    }
  }

  openView = (viewType: IViewTypes) => {
    this._view = viewType
  }

  closeView = () => {
    this._view = 'main'
  }

  reactionAlert = () => {
    this._disposeAlert?.()
    this._disposeAlert = reaction(
      () => this.alert,
      (alert) => {
        if (alert) {
          setTimeout(() => {
            runInAction(() => {
              this.resetAlert()
            })
          }, 5000)
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionView() {
    this._disposeView?.()
    this._disposeView = reaction(
      () => this._view,
      (value) => {
        this._contactIntegrationsStore.setView(value)
      }
    )
  }

  reactionContacts() {
    this._disposeContacts?.()
    this._disposeContacts = reaction(
      () => this.contactsIds,
      (ids) => {
        if (!ids) return

        this._contactsProfileCardStore.setContactsIds(ids)
      }
    )
  }

  reactionConversation() {
    this._disposeConversation?.()
    this._disposeConversation = reaction(
      () => this._conversationId,
      (id) => {
        if (typeof id !== 'number') return

        const item = this.conversationStore.getItem(id)

        if (item) {
          this._contactInfoFormStore.syncFields(item.contact_id)
          this._contactsProfileCardStore.setConversationId(item.id)
          this._contactsProfileCardStore.setInboxId(item.inbox_id)

          const inbox = this._entities.inboxesStore.getItem(item.inbox_id)

          this._contactsConversationOpenStore?.setConversationId(item.id)

          if (inbox) {
            this.setInboxId(inbox.id)
            this._contactsConversationOpenStore?.setInboxId(inbox.id)
          }
        } else {
          this._contactsProfileCardStore.setConversationId(null)
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionContactsDetailsToggle() {
    this._disposeContactsDetailsToggle?.()
    this._disposeContactsDetailsToggle = reaction(
      () => this._contactsDetailsGlobalStore.isCollapse,
      (isCollapse) => {
        if (isCollapse) {
          this._config?.onStopCreateNewContact?.()
          this._config?.onCloseContactDetails?.()
          this.closeView()
        }
      }
    )
  }

  reactionContactUpdated() {
    this._disposeContactUpdated?.()
    this._disposeContactUpdated = reaction(
      () => this._contactId,
      (id) => {
        if (id) {
          this._contactsProfileCardStore.setContactId(id)
          this._contactInfoFormStore.setContactId(id)
          this._contactIntegrationsStore.setContactId(id)
        }
      }
    )
  }

  reactionExternalContactUpdated = () => {
    this._disposeExternalContactUpdated?.()
    this._disposeExternalContactUpdated = reaction(
      () => this._entities.contactsStore.getItem(this._contactId ?? undefined),
      (contact) => {
        if (contact) {
          this.contactInfoFormStore.syncFields(contact.id)
        }
      }
    )
  }

  reorderDraggableItems = async (source: number, target: number) => {
    try {
      const items = cloneDeep(this._draggableItems)
      const [item] = items.splice(source, 1)

      items.splice(target, 0, item)

      runInAction(() => {
        this._draggableItems = items
      })

      await UsersApi.updateUsersUiSettings({
        source_type: 'contacts-details-draggable-items',
        items: items,
      })
    } catch (e) {
      logger.error(e)
    }
  }

  initDraggableItems = async () => {
    try {
      this._cancelRequestInitDraggable.init()

      const { data } = await UsersApi.getUsersUiSettings('contacts-details-draggable-items', {
        cancelToken: this._cancelRequestInitDraggable.token,
      })

      runInAction(() => {
        if (data.data?.items) {
          const savedItems: string[] = data.data.items
          const newItems = defaultDraggableItems.filter((item) => !savedItems.includes(item))
          this._draggableItems = [...savedItems, ...newItems]
        } else {
          this._draggableItems = defaultDraggableItems
        }
      })
    } catch (e) {
      logger.error(e)

      runInAction(() => {
        this._draggableItems = defaultDraggableItems
      })
    }
  }

  get titleHeader() {
    if (this._view === 'notes') return 'Notes'
    if (this._view === 'deals') return 'Deals'
    if (this._view === 'opportunities') return 'Opportunities'
    if (this._view === 'companies') return 'Companies'
    if (this._view === 'conversations-history') return 'Conversations history'
    if (this._view === 'quick-links') return contactsQuickLinksHeader
    if (this._view === 'tickets') return 'Tickets'

    return this._contactsGroupCardStore.isCurrentContact ? 'Contact' : 'Details'
  }

  get isViewTypeNotes() {
    return this._view === 'notes'
  }

  get isViewMain() {
    return this._view === 'main'
  }

  get isViewTypeConversationsHistory() {
    return this._view === 'conversations-history'
  }

  get isViewTypeQuickLinks() {
    return this._view === 'quick-links'
  }

  get isConversationGroup() {
    return this.contactsIds.length > 1
  }

  get isGroupSelectContact() {
    if (!this.isConversationGroup) return true

    return Boolean(this.contact)
  }

  get contactInfoFormStore() {
    return this._contactInfoFormStore
  }

  get conversationsHistoryMessagesStore() {
    return this._conversationsHistoryMessagesStore
  }

  get conversationId() {
    return this._conversationId
  }

  setAlert = (item: IAlert | null) => {
    this._alert = item
  }

  get alert() {
    return this._alert
  }

  get hasAlert() {
    return Boolean(this._alert)
  }

  dispose = () => {
    this._contactId = null
    this._conversationId = null
    this._inboxId = null
    this._variant = 'conversation'

    this._cancelRequestSearchConversation.reset()
    this._cancelRequestInitDraggable.reset()
    this._contactInfoFormStore.dispose()
    this._contactsGroupCardStore.dispose()
    this._contactIntegrationsStore.dispose()
    this.resetAlert()
    this.resetReactions()
  }

  resetAlert = () => {
    this._config?.resetAlert?.()
    this.setAlert(null)
    this._contactInfoFormStore.resetAlert()
    this.contactChoosePhoneModalStore.resetAlert()
  }

  resetReactions = () => {
    this._disposeConversation?.()
    this._disposeAlert?.()
    this._disposeContactsDetailsToggle?.()
    this._disposeContactUpdated?.()
    this._disposeExternalContactUpdated?.()
    this._disposeContacts?.()
    this._disposeView?.()
  }

  get contactsConversationOpenStore() {
    return this._contactsConversationOpenStore
  }

  get contactId(): number | null {
    return this._contactId
  }

  get contactsIds() {
    if (this.conversation) {
      return this.conversation.contactsIds
    }

    return this._contactId ? [this._contactId] : []
  }

  get contact() {
    if (this._contactId === null) return

    return this._entities.contactsStore.getItem(this._contactId)
  }

  get selectedContact() {
    return this.contact
  }

  get contacts() {
    return this.contactsIds.reduce<Contact[]>((state, id) => {
      const contact = this._entities.contactsStore.getItem(id)

      if (contact) state.push(contact)

      return state
    }, [])
  }

  get inbox() {
    return this._entities.inboxesStore.getItem(this._inboxId) as Inbox | undefined
  }

  get conversation() {
    if (this._conversationId === null) return

    return this.conversationStore.getItem(this._conversationId)
  }

  get contactsDetailsGlobalStore() {
    return this._contactsDetailsGlobalStore
  }

  get contactIntegrationsStore() {
    return this._contactIntegrationsStore
  }
}
